
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";

interface DashboardData {
  timelineEntriesCount: number;
  friendsCount: number;
  unreadNotificationsCount: number;
  nextBirthday?: {
    name: string;
    date: string;
  };
  recentActivities: Array<{
    id: string;
    type: string;
    user: string;
    message: string;
    date: string;
  }>;
}

export const useDashboardData = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Real-time subscriptions moved to centralized RealtimeContext

  return useQuery({
    queryKey: ['dashboard-data', user?.id],
    queryFn: async (): Promise<DashboardData> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Timeline entries count
      const { count: timelineEntriesCount } = await supabase
        .from('profile_timeline_entries')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Friends count - use EXACT same logic as useFriends
      const { data: acceptedFriends, error: friendsError } = await supabase
        .from('friends')
        .select('*')
        .or(`and(user_id.eq.${user.id},status.eq.accepted),and(friend_id.eq.${user.id},status.eq.accepted)`)
        .eq('status', 'accepted');

      if (friendsError) {
        console.error('Error fetching friends count:', friendsError);
      }

      // Filter to only show each friendship once (avoid duplicates from bidirectional)
      const uniqueFriends = (acceptedFriends || []).filter(friend => {
        // Show friendship where current user is the user_id (the one who initiated or accepted)
        return friend.user_id === user.id;
      });
      
      const friendsCount = uniqueFriends.length;
      console.log('Dashboard friends count:', friendsCount);

      // Unread notifications count
      const { count: unreadNotificationsCount } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      // Next birthday from friends
      const { data: friendsWithBirthdays } = await supabase
        .from('friends')
        .select(`
          friend_profile:profiles!friends_friend_id_fkey(
            first_name,
            last_name,
            birthdate
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'accepted');

      let nextBirthday;
      if (friendsWithBirthdays && friendsWithBirthdays.length > 0) {
        const today = new Date();
        const currentYear = today.getFullYear();
        
        const upcomingBirthdays = friendsWithBirthdays
          .filter(friend => friend.friend_profile?.birthdate)
          .map(friend => {
            const profile = friend.friend_profile;
            const birthDate = new Date(profile.birthdate);
            const thisYearBirthday = new Date(currentYear, birthDate.getMonth(), birthDate.getDate());
            
            // If birthday has passed this year, check next year
            if (thisYearBirthday < today) {
              thisYearBirthday.setFullYear(currentYear + 1);
            }
            
            return {
              name: profile.first_name || profile.last_name || 'Unbekannt',
              date: thisYearBirthday,
              daysDiff: Math.ceil((thisYearBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
            };
          })
          .sort((a, b) => a.daysDiff - b.daysDiff);

        if (upcomingBirthdays.length > 0) {
          const nextBirthdayData = upcomingBirthdays[0];
          nextBirthday = {
            name: nextBirthdayData.name,
            date: nextBirthdayData.date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
          };
        }
      }

      // Recent activities from notifications
      const { data: notifications } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      const recentActivities = (notifications || []).map(notification => {
        let message = '';
        let user = 'System';
        
        switch (notification.type) {
          case 'new_entry':
            message = 'hat eine neue Erinnerung zu deiner Timeline hinzugefügt';
            user = 'Freund';
            break;
          case 'friend_request':
            message = 'hat dir eine Freundschaftsanfrage gesendet';
            user = 'Jemand';
            break;
          case 'entry_approved':
            message = 'deine Timeline-Erinnerung wurde genehmigt';
            user = 'System';
            break;
          default:
            message = 'Aktivität';
        }

        const timeAgo = getTimeAgo(new Date(notification.created_at));

        return {
          id: notification.id,
          type: notification.type,
          user,
          message,
          date: timeAgo
        };
      });

      return {
        timelineEntriesCount: timelineEntriesCount || 0,
        friendsCount,
        unreadNotificationsCount: unreadNotificationsCount || 0,
        nextBirthday,
        recentActivities
      };
    },
    enabled: !!user?.id
  });
};

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInDays > 0) {
    return `vor ${diffInDays} Tag${diffInDays > 1 ? 'en' : ''}`;
  } else if (diffInHours > 0) {
    return `vor ${diffInHours} Stunde${diffInHours > 1 ? 'n' : ''}`;
  } else {
    return 'vor wenigen Minuten';
  }
}
